/**
 * The swipe screen shared by every real-or-fake deck. Drag the card right for REAL, left for
 * FAKE (or tap the two buttons, or press ← / →). The card tilts with the drag and flies off
 * on commit; the reveal shows on the way out.
 */
import { SwipeRound, type Card, type Deck, type SwipeOutcome, type SwipeRules } from './engine';
import type { GameContext, GameDef } from '../registry';
import { el, toast } from '../../ui/dom';
import { showResult } from '../../ui/result';
import { formatTime } from '../../ui/format.ts';

export interface SwipeConfig {
  def: Omit<GameDef, 'mount'>;
  /** where the deck JSON lives (under public/) */
  deckUrl: string;
  /** button labels */
  realLabel: string;
  fakeLabel: string;
  intro: string;
  rules?: Partial<SwipeRules>;
}

const COMMIT_PX = 90;
const deckCache = new Map<string, Promise<Deck>>();

function loadDeck(url: string): Promise<Deck> {
  let p = deckCache.get(url);
  if (!p) {
    p = fetch(url).then((r) => {
      if (!r.ok) throw new Error(`${r.status}`);
      return r.json() as Promise<Deck>;
    });
    deckCache.set(url, p);
    p.catch(() => deckCache.delete(url));
  }
  return p;
}

export function mountSwipe(cfg: SwipeConfig, ctx: GameContext): () => void {
  const { root, sfx, buzz, player } = ctx;
  let game: SwipeRound | null = null;
  let deck: Deck | null = null;
  let raf = 0;
  let running = false;
  let lastClock = '';
  const timers = new Set<ReturnType<typeof setTimeout>>();
  const later = (fn: () => void, ms: number) => {
    const t = setTimeout(() => {
      timers.delete(t);
      fn();
    }, ms);
    timers.add(t);
  };

  // ---- DOM --------------------------------------------------------------------
  const clockEl = el('div', { class: 'ts-clock' }, '0.0s');
  const progressEl = el('div', { class: 'sw-progress' }, '0 / 0');
  const mistakesEl = el('div', { class: 'ts-mist' }, '✕ 0');
  const windowFill = el('div', { class: 'fill' });
  const stack = el('div', { class: 'sw-stack' });
  const table = el('div', { class: 'sw-table' }, el('div', { class: 'sw-side sw-fake' }, '✕'), stack, el('div', { class: 'sw-side sw-real' }, '✓'), el('div', { class: 'ts-window sw-window' }, windowFill));
  const fakeBtn = el('button', { class: 'sw-btn fake', type: 'button' }, el('span', { class: 'glyph' }, '✕'), el('small', {}, cfg.fakeLabel));
  const realBtn = el('button', { class: 'sw-btn real', type: 'button' }, el('span', { class: 'glyph' }, '✓'), el('small', {}, cfg.realLabel));
  const overlay = el('div', { class: 'ts-overlay' });
  const wrap = el(
    'section',
    { class: 'ts sw' },
    el('header', { class: 'ts-top' }, el('button', { class: 'icon-btn', type: 'button', 'aria-label': 'pause', onclick: () => togglePause() }, '❚❚'), clockEl, mistakesEl),
    table,
    progressEl,
    el('div', { class: 'sw-btns' }, fakeBtn, realBtn),
    overlay,
  );
  root.append(wrap);

  function cardEl(card: Card): HTMLElement {
    return el('div', { class: 'sw-card' }, el('div', { class: 'sw-text' }, card.text), card.sub ? el('div', { class: 'sw-sub' }, card.sub) : null, el('div', { class: 'sw-stamp real' }, cfg.realLabel), el('div', { class: 'sw-stamp fake' }, cfg.fakeLabel));
  }

  function render(): void {
    if (!game) return;
    stack.replaceChildren();
    const nxt = game.next;
    if (nxt) {
      const under = cardEl(nxt);
      under.classList.add('under');
      stack.append(under);
    }
    const cur = game.current;
    if (cur) {
      const top = cardEl(cur);
      top.classList.add('top', 'deal-in');
      stack.append(top);
    }
    progressEl.textContent = `${game.done + 1} / ${game.total}`;
    mistakesEl.textContent = `✕ ${game.mistakes}`;
  }

  // ---- outcome ------------------------------------------------------------------
  function handle(o: SwipeOutcome): void {
    const top = stack.querySelector<HTMLElement>('.sw-card.top');
    if (top) {
      top.classList.remove('top', 'deal-in');
      top.style.transition = 'transform 0.35s ease-in, opacity 0.35s ease-in';
      const dir = o.did === 'timeout' ? 0 : o.did ? 1 : -1;
      top.style.transform = dir === 0 ? 'translateY(60vh) rotate(6deg)' : `translateX(${dir * 120}vw) rotate(${dir * 25}deg)`;
      top.style.opacity = '0';
      top.classList.add(o.ok ? 'was-ok' : 'was-bad');
      later(() => top.remove(), 380);
    }
    if (o.ok) {
      sfx.tick();
      buzz.light();
      toast(wrap, o.card.reveal ?? (o.card.real ? 'Real' : 'Made up'), 'ok', 1100);
    } else {
      sfx.buzz();
      buzz.error();
      const pen = el('div', { class: 'ts-penalty' }, `+${(game!.rules.penaltyMs / 1000).toFixed(1)}s`);
      clockEl.append(pen);
      later(() => pen.remove(), 900);
      const why = o.did === 'timeout' ? 'Too slow! ' : '';
      toast(wrap, why + (o.card.reveal ?? (o.card.real ? 'It was real' : 'It was made up')), 'bad', 2000);
    }
    if (o.won) void finish();
    else render();
  }

  function answer(real: boolean): void {
    if (!running || !game || game.paused) return;
    const o = game.answer(real, performance.now());
    if (o) handle(o);
  }

  // ---- drag ---------------------------------------------------------------------------
  let drag: { id: number; x0: number; y0: number; el: HTMLElement } | null = null;
  stack.addEventListener('pointerdown', (e) => {
    const top = stack.querySelector<HTMLElement>('.sw-card.top');
    if (!top || !running || game?.paused || drag) return;
    e.preventDefault();
    stack.setPointerCapture(e.pointerId);
    drag = { id: e.pointerId, x0: e.clientX, y0: e.clientY, el: top };
    top.classList.remove('deal-in');
    top.style.transition = 'none';
  });
  stack.addEventListener('pointermove', (e) => {
    if (!drag || e.pointerId !== drag.id) return;
    const dx = e.clientX - drag.x0;
    const dy = (e.clientY - drag.y0) * 0.3;
    drag.el.style.transform = `translate(${dx}px, ${dy}px) rotate(${dx / 14}deg)`;
    const k = Math.min(1, Math.abs(dx) / COMMIT_PX);
    drag.el.style.setProperty('--real', dx > 0 ? String(k) : '0');
    drag.el.style.setProperty('--fake', dx < 0 ? String(k) : '0');
    table.classList.toggle('lean-real', dx > COMMIT_PX / 2);
    table.classList.toggle('lean-fake', dx < -COMMIT_PX / 2);
  });
  const endDrag = (e: PointerEvent) => {
    if (!drag || e.pointerId !== drag.id) return;
    const dx = e.clientX - drag.x0;
    const el_ = drag.el;
    drag = null;
    table.classList.remove('lean-real', 'lean-fake');
    if (Math.abs(dx) >= COMMIT_PX) {
      answer(dx > 0);
    } else {
      el_.style.transition = 'transform 0.2s ease';
      el_.style.transform = '';
      el_.style.setProperty('--real', '0');
      el_.style.setProperty('--fake', '0');
    }
  };
  stack.addEventListener('pointerup', endDrag);
  stack.addEventListener('pointercancel', endDrag);
  stack.addEventListener('contextmenu', (e) => e.preventDefault());
  realBtn.addEventListener('pointerdown', (e) => {
    e.preventDefault();
    answer(true);
  });
  fakeBtn.addEventListener('pointerdown', (e) => {
    e.preventDefault();
    answer(false);
  });
  function onKey(e: KeyboardEvent): void {
    if (e.repeat) return;
    if (e.key === 'Escape') togglePause();
    else if (e.key === 'ArrowRight') answer(true);
    else if (e.key === 'ArrowLeft') answer(false);
  }
  window.addEventListener('keydown', onKey);

  // ---- clock ------------------------------------------------------------------------------
  function frame(now: number): void {
    if (!running || !game) return;
    const o = game.tick(now);
    if (o) handle(o);
    if (!running || !game) return;
    const clock = formatTime(game.elapsed(now));
    if (clock !== lastClock) {
      clockEl.firstChild!.textContent = clock;
      lastClock = clock;
    }
    const r = game.remaining(now);
    windowFill.style.transform = `scaleX(${r.toFixed(3)})`;
    windowFill.classList.toggle('low', r < 0.25);
    raf = requestAnimationFrame(frame);
  }

  // ---- start / pause / finish --------------------------------------------------------------
  function ready(): void {
    const go = el('button', { class: 'btn primary big', type: 'button', disabled: true }, 'Loading deck…');
    overlay.replaceChildren(
      el(
        'div',
        { class: 'sheet' },
        el('h2', {}, `${cfg.def.icon} ${cfg.def.title}`),
        el('p', {}, cfg.intro),
        el('p', { class: 'dim' }, `Swipe right for ${cfg.realLabel.toLowerCase()}, left for ${cfg.fakeLabel.toLowerCase()}. ${cfg.rules?.cards ?? 24} cards, ${((cfg.rules?.windowMs ?? 6000) / 1000).toFixed(0)} s each. Wrong or late: +${((cfg.rules?.penaltyMs ?? 3000) / 1000).toFixed(0)} s.`),
        player.practice ? el('p', { class: 'dim' }, 'Practice mode: nothing is posted.') : null,
        go,
        el('button', { class: 'btn ghost', type: 'button', onclick: () => ctx.exit() }, 'Back'),
      ),
    );
    overlay.classList.add('show');
    loadDeck(cfg.deckUrl)
      .then((d) => {
        deck = d;
        go.disabled = false;
        go.textContent = 'Ready';
        go.addEventListener('click', () => countdown());
      })
      .catch(() => {
        go.textContent = 'Could not load the deck — are you online?';
      });
  }

  function countdown(): void {
    sfx.unlock();
    let n = 3;
    const big = el('div', { class: 'count' }, String(n));
    overlay.replaceChildren(big);
    const step = () => {
      n--;
      if (n <= 0) {
        overlay.classList.remove('show');
        overlay.replaceChildren();
        start();
        return;
      }
      big.textContent = String(n);
      big.classList.remove('pop');
      void big.offsetWidth;
      big.classList.add('pop');
      sfx.tick();
      later(step, 600);
    };
    sfx.tick();
    later(step, 600);
  }

  function start(): void {
    game = new SwipeRound(deck!.cards, cfg.rules);
    game.start(performance.now());
    running = true;
    lastClock = '';
    render();
    raf = requestAnimationFrame(frame);
  }

  function togglePause(): void {
    if (!running || !game) return;
    if (game.paused) {
      overlay.classList.remove('show');
      overlay.replaceChildren();
      game.resume(performance.now());
      raf = requestAnimationFrame(frame);
    } else {
      game.pause(performance.now());
      cancelAnimationFrame(raf);
      overlay.replaceChildren(
        el(
          'div',
          { class: 'sheet' },
          el('h2', {}, 'Paused'),
          el('p', { class: 'dim' }, `${formatTime(game.elapsed(performance.now()))} · ${game.mistakes} mistakes · card ${game.done + 1} of ${game.total}`),
          el('button', { class: 'btn primary big', type: 'button', onclick: () => togglePause() }, 'Resume'),
          el('button', { class: 'btn', type: 'button', onclick: () => restart() }, 'Restart'),
          el('button', { class: 'btn ghost', type: 'button', onclick: () => ctx.exit() }, 'Quit'),
        ),
      );
      overlay.classList.add('show');
    }
  }

  function restart(): void {
    running = false;
    cancelAnimationFrame(raf);
    overlay.classList.remove('show');
    overlay.replaceChildren();
    stack.replaceChildren();
    countdown();
  }

  async function finish(): Promise<void> {
    if (!game) return;
    running = false;
    cancelAnimationFrame(raf);
    const score = Math.round(game.elapsed(performance.now()));
    clockEl.firstChild!.textContent = formatTime(score);
    windowFill.style.transform = 'scaleX(0)';
    sfx.win();
    buzz.success();
    const result = { score, mistakes: game.mistakes, detail: { bestStreak: game.bestStreak, cards: game.total, deck: deck?.version }, practice: player.practice };
    const { board, newBest } = await ctx.finish(result);
    showResult(overlay, { game: cfg.def, result, board, newBest, player, post: () => ctx.post(result), again: () => restart(), board_: () => ctx.showBoard(), menu: () => ctx.exit() });
  }

  function onHidden(): void {
    if (document.hidden && running && game && !game.paused) togglePause();
  }
  document.addEventListener('visibilitychange', onHidden);
  ctx.setBackHandler(() => {
    if (running && game && !game.paused) {
      togglePause();
      return true;
    }
    return false;
  });

  ready();

  return () => {
    running = false;
    cancelAnimationFrame(raf);
    for (const t of timers) clearTimeout(t);
    window.removeEventListener('keydown', onKey);
    document.removeEventListener('visibilitychange', onHidden);
    ctx.setBackHandler(null);
    wrap.remove();
  };
}
